import { ELCLogger, TriggerType } from '@estee/elc-logging';
import serviceNames from '~setup/VtoService';
import { diContainer } from '~setup/diContainer';
import { surveyTypes } from '~constants/Survey';
import { PersonalizationRepository } from '~repositories/PersonalizationRepository';
import { UserInfoRepository } from '~repositories/UserInfoRepository';
import { setVtoSurveyStorage } from '~utils/setVtoSurveyStorage';
import { getVtoSurveyStorage } from '~utils/getVtoSurveyStorage';
import { VtoTypes } from '~constants/Vto';
import { Personalization } from '~domain/entities/Personalization';

interface ISurveyUseCase {
    vtoType: string;
}

const { name: serviceName, version: serviceVersion } = __serviceInfo__;

export class SurveyUseCase {
    private personalizationRepository: PersonalizationRepository;
    private userInfoRepository: UserInfoRepository;
    private logger: ELCLogger;
    private vtoType: VtoTypes;

    constructor({ vtoType }: ISurveyUseCase) {
        this.personalizationRepository = diContainer.get(serviceNames.personalizationRepository);
        this.userInfoRepository = diContainer.get(serviceNames.userInfoRepository);
        this.logger = new ELCLogger({ serviceName, serviceVersion });
        this.vtoType = vtoType as VtoTypes;
    }

    public execute = async () => {
        const surveyStorage = getVtoSurveyStorage(this.vtoType);

        if (surveyStorage?.isSurveyTaken) {
            return;
        }

        await this.updateBrowserStorageWithApiData();
    };

    private async updateBrowserStorageWithApiData() {
        let userId;
        const vtoType = this.vtoType;

        try {
            const userData = await this.userInfoRepository.getUser();

            userId = userData.userId || userData.sessionId;

            if (!userId) {
                setVtoSurveyStorage({ vtoType, data: { isSurveyTaken: false } });

                return;
            }

            const surveyType = surveyTypes[vtoType];

            const data = (await this.personalizationRepository.getPersonalizationDataByPerlgemUserId(
                [surveyType],
                userId
            )) as Personalization;

            const storageData = this.getStorageData(data);

            setVtoSurveyStorage(storageData);
        } catch (error) {
            userId && setVtoSurveyStorage({ vtoType, data: { isP13nApiVerified: true } });

            this.logger.error({
                triggerType: TriggerType.api,
                payload: { error },
                message: `${error.message}`
            });
        }
    }

    private getStorageData(data: Personalization) {
        const vtoType = this.vtoType;
        let storageData = { vtoType, data: { isSurveyTaken: false, isP13nApiVerified: true } };

        if (Array.isArray(data)) {
            const surveyData = data.filter((survey) => survey.tags.includes(surveyTypes[vtoType]));

            if (surveyData.length > 0) {
                storageData = { vtoType, data: { isSurveyTaken: true, isP13nApiVerified: true } };
            }
        }

        return storageData;
    }
}
